import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Film } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import ImportFromRecordingPanel from "@/components/creator/ImportFromRecordingPanel";

const ACCEPTED_TYPES = ["video/mp4", "video/webm", "video/quicktime"];

export default function VideoUploadForm({ courseId, onUploaded }) {
  const [mode, setMode] = useState("upload"); // "upload" | "import"
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!title.trim()) { toast.error("Give this lecture a title"); return; }
    if (!file) { toast.error("Choose a video file"); return; }
    if (!ACCEPTED_TYPES.includes(file.type)) { toast.error("Only .mp4, .webm, or .mov files are supported"); return; }

    const formData = new FormData();
    formData.append("title", title.trim());
    formData.append("file", file);

    setUploading(true);
    setProgress(0);
    try {
      await api.post(`/courses/${courseId}/videos`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success("Video uploaded! AI is generating notes in the background.");
      setTitle("");
      setFile(null);
      onUploaded();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-5" data-testid="video-upload-form">
      <div className="flex items-center gap-1 mb-4 bg-white/5 rounded-lg p-1 w-fit">
        <button onClick={() => setMode("upload")} data-testid="video-upload-mode-upload-btn"
          className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${mode === "upload" ? "bg-pink-500 text-white" : "text-slate-400 hover:text-white"}`}>
          <Upload className="w-3.5 h-3.5" /> Upload File
        </button>
        <button onClick={() => setMode("import")} data-testid="video-upload-mode-import-btn"
          className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${mode === "import" ? "bg-pink-500 text-white" : "text-slate-400 hover:text-white"}`}>
          <Film className="w-3.5 h-3.5" /> Import from Meet Recordings
        </button>
      </div>

      {mode === "import" ? (
        <ImportFromRecordingPanel courseId={courseId} onImported={onUploaded} />
      ) : (
        <div className="space-y-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Lecture title" data-testid="video-upload-title-input"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-pink-400/50" disabled={uploading} />
          <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="video-upload-file-input"
            className="w-full text-sm text-slate-400 file:bg-white/10 file:text-white file:border-0 file:rounded-lg file:px-3 file:py-2 file:mr-3 file:text-xs file:font-semibold" disabled={uploading} />
          {uploading && (
            <div data-testid="video-upload-progress-bar">
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-pink-400 transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-slate-400 mt-1">{progress}% uploaded...</p>
            </div>
          )}
          <button onClick={handleUpload} disabled={uploading} data-testid="video-upload-submit-btn"
            className="flex items-center gap-2 bg-pink-500 hover:bg-pink-400 disabled:opacity-60 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading..." : "Upload Video"}
          </button>
          <p className="text-[11px] text-slate-500">Max 800MB. AI transcription supports up to ~45-50 minutes of audio per video.</p>
        </div>
      )}
    </div>
  );
}
